import { get, post, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import type { PurgePreview, PurgeResult, RetentionUnit } from '../../types/retention';

export type NotificationChannel = 'sms' | 'email' | 'in_app' | 'whatsapp';
export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'read';

export interface NotificationLog {
  id: string;
  userId?: string;
  /** Login handle of the recipient user (resolved by the API). */
  username?: string;
  /** Display name (first + last) of the recipient user (resolved by the API). */
  userName?: string;
  patientId?: string;
  channel: NotificationChannel;
  notificationType: string;
  /** Phone number or email the message was delivered to. */
  recipient: string;
  content: string;
  status: NotificationStatus;
  errorMessage?: string;
  sentAt?: string;
  readAt?: string;
  referenceId?: string;
  referenceType?: string;
  createdAt: string;
}

export const notificationService = {
  getNotifications: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<NotificationLog[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<NotificationLog[]>(`/notifications?${params.toString()}`) as Promise<
      ApiResponse<NotificationLog[]> & { meta: PaginationMeta }
    >;
  },

  createNotification: (data: any): Promise<ApiResponse<NotificationLog>> => {
    return post<NotificationLog>('/notifications', data);
  },

  deleteNotification: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/notifications/${id}`);
  },

  bulkDeleteNotifications: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/notifications/bulk-delete', { ids });
  },

  /** How many notifications a purge with the given retention window would remove. */
  previewPurgeNotifications: (value: number, unit: RetentionUnit): Promise<ApiResponse<PurgePreview>> => {
    return get<PurgePreview>('/notifications/purge/preview', { value, unit });
  },

  /** Permanently delete notifications older than the retention window. */
  purgeNotifications: (value: number, unit: RetentionUnit): Promise<ApiResponse<PurgeResult>> => {
    return post<PurgeResult>('/notifications/purge', { value, unit });
  },
};
